// The crash marker's colours and its popup body — shared by the Safety Dashboard map
// and the Safety Overview map so a crash reads the same on both: its record, the
// deficiencies at its location in plain sentences, the countermeasure for each and
// the report links.
import React from 'react';
import { findingSentence, fmtUsd, visibleFindings } from './insightsText';
import { recommendationForCrash, starsText } from './cmfBenefit';

export const CRASH_COLORS = { fatal: '#7e22ce', injury: '#e03131', pdo: '#868e96', unknown: '#adb5bd' };
export const CRASH_RADIUS = { fatal: 8, injury: 6, pdo: 4.5, unknown: 4.5 };

const fmtMp = (v) => (v === null || v === undefined ? '—' : v.toFixed(3));

/** Leaflet path options of a crash marker (animal strikes hollow). */
export const crashPathOptions = (c) => (c.isAnimal
  ? { color: '#9aa7ac', weight: 1.6, fillOpacity: 0 }
  : { color: '#ffffff', weight: 1.2, fillColor: CRASH_COLORS[c.severityClass], fillOpacity: 0.95 });

export const crashRadius = (c) => (c.isAnimal ? 4 : CRASH_RADIUS[c.severityClass]);

// every field of a crash record (crash_sources fieldMap), in reading order
export const CRASH_FIELDS = [
  ['id', 'Crash number'], ['severity', 'Severity'], ['maxInjury', 'Most severe injury'],
  ['numFatal', 'Fatal injuries'], ['numNonfatal', 'Non-fatal injuries'], ['manner', 'Manner of collision'],
  ['firstHarmful', 'First harmful event'], ['numVehicles', 'Vehicles'], ['light', 'Light'], ['weather', 'Weather'],
  ['surface', 'Road surface'], ['junction', 'Junction type'], ['trafficControl', 'Traffic control'],
  ['speedLimit', 'Speed limit'], ['roadway', 'Roadway'], ['nearInt', 'Near intersection roadway'],
  ['city', 'City / town'], ['district', 'MassDOT district'],
];

export default function CrashPopupBody({ crash: c, analysis, recommendations = [] }) {
  return (
    <div className="safety-popup">
      <strong>{c.manner || 'Crash'}</strong>
      <div className="safety-popup-mp">
        {c.road ? `${c.road} · ` : ''}MP {fmtMp(c.milepost)} · {c.city || '—'} · {c.datetime ? c.datetime.slice(0, 16).replace('T', ' ') : '—'}
      </div>
      <table><tbody>
        {/* the whole crash record the snapshot keeps — every field, '—' where the record has none */}
        {CRASH_FIELDS.map(([k, label]) => {
          const v = k === 'id' ? (c.crashId ?? c.id) : c[k];   // the Safety Overview keeps the number in crashId
          return <tr key={k}><td>{label}</td><td>{v === null || v === undefined || v === '' ? '—' : String(v)}</td></tr>;
        })}
        <tr><td>Nearest run</td>
          <td>{(() => { const m = c.nearestRunMi || {}; const v = analysis === 'ALL'
            ? Object.entries(m).sort((a, b) => a[1] - b[1])[0]
            : (m[analysis] != null ? [analysis, m[analysis]] : null);
          return v ? `${v[1]} mi (${v[0]})` : '—'; })()}</td></tr>
        {c.nearestIntersectionFt != null && (
          <tr><td>Intersection</td>
            <td>{c.nearestIntersectionFt} ft{c.insideIsd ? ' — inside required ISD' : ''}</td></tr>
        )}
      </tbody></table>
      {visibleFindings(c, analysis).length > 0 && (
        <div className="safety-popup-findings">
          <strong>Deficiencies at this location</strong>
          <ul>
            {visibleFindings(c, analysis).map(([a, f]) => (
              <li key={a}>
                <b>{a}</b> — {findingSentence(a, f, c)}
                {f.reportUrl && (
                  <> <a href={f.reportUrl} target="_blank" rel="noreferrer">Report ↗</a></>
                )}
                {(() => {
                  const r = recommendationForCrash(recommendations, c, a);
                  if (!r) return null;
                  return (
                    <div className="safety-popup-cmf">
                      ↳ <b>{r.entry.name}</b> · CMF {r.entry.cmf}{' '}
                      <span className="safety-stars">{starsText(r.entry.stars)}</span>
                      {r.entry.fallback ? <span className="safety-fallback">3★</span> : null}
                      {' · '}{r.matchCount} matching crash{r.matchCount === 1 ? '' : 'es'} at this site
                      {' → '}{r.preventedPerYr} prevented/yr ·{' '}
                      <span className="safety-benefit">{fmtUsd(r.benefitPerYr)}/yr</span>
                    </div>
                  );
                })()}
              </li>
            ))}
          </ul>
        </div>
      )}
      {c.isEmbankment && <em>Embankment / overturn event (GRW signature).</em>}
      {c.isAnimal && <em>Animal strike — SSD correlation is case-dependent.</em>}
      {(() => {
        const links = Object.entries(c.findings || {}).filter(([, f]) => f?.reportUrl);
        if (!links.length) return null;
        return (
          <div className="safety-popup-actions">
            {links.map(([a, f]) => (
              <a key={a} className="safety-btn safety-report-btn" href={f.reportUrl} target="_blank" rel="noreferrer">
                Open {a} report at MP {fmtMp(c.milepost)} ↗
              </a>
            ))}
          </div>
        );
      })()}
    </div>
  );
}
